import React, { useEffect, useState } from 'react'
import { string, shape, object, func, bool } from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { sortableContainer, sortableElement } from 'react-sortable-hoc'
import { Form, Select, Typography, Checkbox, Space } from 'antd'
import { DeleteOutlined, DownOutlined, UpOutlined } from '@ant-design/icons'
import styled from 'styled-components'

import { getDatabaseTexts } from 'states/modules/internalDatabasesField'
import { filterText } from 'services/filter'
import InfoField from 'components/infoField'
import Loader from 'components/loader'
import { LoaderContainer } from 'components/documentPreview/styles'
import DOMPurify from 'dompurify'

const { Paragraph } = Typography

const SortableElement = sortableElement(({ children }) => children)
const SortableContainer = sortableContainer(({ children }) => (
	<div>{children}</div>
))

function ExpandableItem({ option }) {
	const [isOpen, setIsOpen] = useState(false)

	return (
		<div style={{ display: 'flex', flexDirection: 'column' }}>
			<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
				<Checkbox value={option.id}>{option.description}</Checkbox>
				<button
					onClick={() => setIsOpen(!isOpen)}
					style={{
						cursor: 'pointer',
						fontSize: '14px',
						background: 'none',
						border: 'none',
						padding: 0,
						display: 'flex',
						alignItems: 'center',
					}}
					aria-expanded={isOpen}>
					{isOpen ? <UpOutlined /> : <DownOutlined />}
				</button>
			</div>
			{isOpen && (
				<DescriptionContainer>
					<p
						dangerouslySetInnerHTML={{
							__html: DOMPurify.sanitize(option.text),
						}}
					/>
				</DescriptionContainer>
			)}
		</div>
	)
}

ExpandableItem.propTypes = {
	option: shape({
		id: string.isRequired,
		description: string.isRequired,
		text: string.isRequired,
	}).isRequired,
}

const TextDatabaseField = ({
	pageFieldsData,
	inputValue,
	disabled,
	onChange,
	form,
	visible,
	className,
	first,
}) => {
	const dispatch = useDispatch()
	const {
		databaseId,
		variable,
		type,
		id,
		list,
		label,
		optional,
		typeWidget,
		minimum,
		maximum,
	} = pageFieldsData
	const isObj = typeof variable === 'object'
	const varname = isObj ? variable.name : variable
	const name = id !== undefined ? `${varname}_${id}` : varname

	const [values, setValues] = useState(inputValue || [])

	const dataItem = useSelector(
		({ internalDatabaseField }) => internalDatabaseField.data[databaseId]
	)

	useEffect(() => {
		dispatch(getDatabaseTexts({ id: databaseId, perPage: 100 }))
	}, [dispatch, databaseId])

	useEffect(() => {
		if (form !== undefined) {
			form.setFieldsValue({
				[name]: values,
			})
		}
	}, [form, name, values])

	const handleChangeSelect = (e) => {
		setValues((prev) => {
			const newValues = [...prev, e]
			onChange(newValues)
			return newValues
		})
	}

	const handleDeleteSelectedText = (e) => {
		setValues((prev) => {
			const newValues = prev.filter((item) => item !== e)
			onChange(newValues)
			return newValues
		})
		// setValues(newValues)
	}

	const handleSort = ({ oldIndex, newIndex }) => {
		let newValues = [...values]
		newValues.splice(newIndex, 0, newValues.splice(oldIndex, 1)[0])

		setValues(newValues)
	}

	const options =
		dataItem?.texts?.filter((item) => !values.includes(item.id)) ?? [] // filter selected options

	const selectedItems =
		(dataItem?.texts &&
			values.map((id) => dataItem.texts.find((item) => item.id === id))) ||
		[]

	const notFoundContentText =
		dataItem === undefined
			? 'Nenhum dado encontrado'
			: dataItem.loading
				? 'Dados sendo carregados'
				: 'Todos os dados foram ultilizados'

	const renderList = () => {
		return (
			<>
				<SortableContainer onSortEnd={handleSort} distance={15}>
					{selectedItems.map(({ id, description }, index) => (
						<SortableElement key={id} index={index}>
							<TextSelected>
								<TextTitle>{description}</TextTitle>
								<DeleteOutlined
									style={{
										height: '100%',
										color: 'var(--ant-primary-color)',
										fontSize: 20,
									}}
									onClick={() => handleDeleteSelectedText(id)}
								/>
							</TextSelected>
						</SortableElement>
					))}
				</SortableContainer>

				<Select
					showSearch
					maxTagCount={1}
					value={null}
					disabled={disabled}
					showArrow={false}
					filterOption={filterText}
					placeholder="Adicionar Texto"
					onChange={handleChangeSelect}
					autoFocus={first}
					notFoundContent={notFoundContentText}
					options={options.map((item, i) => {
						return { value: item.id, label: item.description }
					})}
				/>
			</>
		)
	}

	const renderCheckBox = () => {
		return (
			<Checkbox.Group disabled={disabled} onChange={onChange}>
				<Space direction="vertical" size={12} style={{ width: '100%' }}>
					{options?.map((option, index) => (
						<ExpandableItem key={index} option={option} />
					))}
				</Space>
			</Checkbox.Group>
		)
	}

	const renderFormItem = () => {
		let info = ''
		if (minimum) {
			info = `Select at least ${minimum}`
		}
		if (maximum) {
			info = `Select maximum ${maximum}`
		}
		if (minimum && maximum) {
			info = `Select at least ${minimum} and at most ${maximum}`
		}

		return (
			<Form.Item
				key={name}
				style={{ width: '100%' }}
				type={type}
				className={className}
				label={<InfoField label={label} info={info} />}
				name={list !== undefined ? [list, name] : name}
				initialValue={values}
				rules={
					visible && [
						() => ({
							validator(rule, value) {
								const max = Number(maximum) || undefined
								const min = Number(minimum) || undefined

								if (min && min > value.length)
									return Promise.reject(`Selecione no minímo ${min}.`)

								if (max && value.length > max)
									return Promise.reject(`Selecione no máximo ${max}.`)

								if (optional) return Promise.resolve()

								if (value.length === 0)
									return Promise.reject('Este campo é obrigatório.')

								return Promise.resolve()
							},
						}),
					]
				}
				colon={false}>
				{dataItem?.loading ? (
					<LoaderContainer>
						<Loader />
					</LoaderContainer>
				) : typeWidget === 'checkbox' ? (
					renderCheckBox()
				) : (
					renderList()
				)}
			</Form.Item>
		)
	}
	return renderFormItem()
}

ExpandableItem.propTypes = {
	option: shape({
		id: string.isRequired,
		description: string.isRequired,
		text: string.isRequired,
	}).isRequired,
}

TextDatabaseField.propTypes = {
	pageFieldsData: shape({
		label: string.isRequired,
		variable: object.isRequired,
		type: string.isRequired,
	}).isRequired,
	className: string,
	onChange: func,
	first: bool,
	inputValue: string,
	disabled: bool,
	visible: bool,
	form: object,
}

export default TextDatabaseField

const DescriptionContainer = styled.div`
	max-height: 100px;
	overflow-y: auto;
	padding-right: 5px;
	text-align: justify;
	margin-top: 5px;
	color: #555;
	margin-left: 20px;
`

const TextSelected = styled.div`
	width: 100%;
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	border: 1px solid #d9d9d9;
	padding: 20px;
	margin-bottom: 15px;
	background: white;
`

const TextTitle = styled(Paragraph)`
	width: 90%;
	margin: 0 !important;

	font-size: 16px;
`
